import React from 'react';
import { cn } from './cn';
import type { Severity } from '@/types';
import { SEVERITY, severityKey } from './severity';

export type BadgeTone =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'critical'
  | 'info';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Small 6px leading dot. */
  dot?: boolean;
  /** Tighter padding. */
  compact?: boolean;
  /** Render with a dashed border instead of a solid ring — for "pending"/"not applicable" states. */
  dashed?: boolean;
}

const TONE: Record<BadgeTone, string> = {
  neutral:  'bg-surface-2 text-ink-muted ring-1 ring-inset ring-border',
  brand:    'bg-brand-soft text-brand ring-1 ring-inset ring-brand/20',
  success:  'bg-sev-maintenance-soft text-sev-maintenance-ink ring-1 ring-inset ring-sev-maintenance/20',
  warning:  'bg-sev-major-soft text-sev-major-ink ring-1 ring-inset ring-sev-major/20',
  critical: 'bg-sev-critical-soft text-sev-critical-ink ring-1 ring-inset ring-sev-critical/25',
  info:     'bg-sev-minor-soft text-sev-minor-ink ring-1 ring-inset ring-sev-minor/20',
};

const DASHED_TONE: Record<BadgeTone, string> = {
  neutral:  'bg-transparent text-ink-subtle border border-dashed border-border',
  brand:    'bg-transparent text-brand border border-dashed border-brand/40',
  success:  'bg-transparent text-sev-maintenance-ink border border-dashed border-sev-maintenance/40',
  warning:  'bg-transparent text-sev-major-ink border border-dashed border-sev-major/40',
  critical: 'bg-transparent text-sev-critical-ink border border-dashed border-sev-critical/40',
  info:     'bg-transparent text-sev-minor-ink border border-dashed border-sev-minor/40',
};

const DOT_TONE: Record<BadgeTone, string> = {
  neutral: 'bg-ink-subtle', brand: 'bg-brand', success: 'bg-sev-maintenance',
  warning: 'bg-sev-major',  critical: 'bg-sev-critical', info: 'bg-sev-minor',
};

export const Badge: React.FC<BadgeProps> = ({
  tone = 'neutral', dot = false, compact = false, dashed = false, className, children, ...rest
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap',
      compact ? 'px-1.5 py-0.5 text-[10.5px]' : 'px-2 py-0.5 text-caption',
      dashed ? DASHED_TONE[tone] : TONE[tone],
      className,
    )}
    {...rest}
  >
    {dot && <span className={cn('h-1.5 w-1.5 rounded-full', DOT_TONE[tone])} aria-hidden />}
    {children}
  </span>
);

/**
 * Severity pill — preferred over <Badge> for severity display. Always uses
 * the severity token palette and includes a solid dot prefix.
 */
export interface SeverityPillProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  severity: Severity;
  compact?: boolean;
  hideDot?: boolean;
  /** Override the label text entirely. */
  labelOverride?: string;
}

export const SeverityPill: React.FC<SeverityPillProps> = ({
  severity, compact = false, hideDot = false, labelOverride, className, ...rest
}) => {
  const token = SEVERITY[severityKey(severity)];
  const label = labelOverride ?? token.label;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap',
        compact ? 'px-1.5 py-0.5 text-[10.5px]' : 'px-2 py-0.5 text-caption',
        token.classes.pill,
        token.classes.ring,
        className,
      )}
      {...rest}
    >
      {!hideDot && <span className={cn('h-1.5 w-1.5 rounded-full', token.classes.dot)} aria-hidden />}
      {label}
    </span>
  );
};

/** Atomic dot — useful for inline finding lists. */
export const SeverityDot: React.FC<{ severity: Severity; className?: string; size?: 'sm' | 'md' }> = ({
  severity, className, size = 'md',
}) => {
  const token = SEVERITY[severityKey(severity)];
  return (
    <span
      className={cn(
        'inline-block rounded-full flex-shrink-0',
        size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
        token.classes.dot,
        className,
      )}
      aria-label={token.label}
    />
  );
};
